import React from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChartDataPoint } from '../../hooks/useStatistics';

interface Props {
  data: ChartDataPoint[];
  loading: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const value = payload[0]?.value || 0;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
      <p className="text-sm font-semibold text-gray-900">
        Semaine du {label} : <span className="text-rose-500">{value} RDV pris</span>
      </p>
    </div>
  );
};

const StatisticsEvolutionChart: React.FC<Props> = ({ data, loading }) => {
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="h-5 bg-gray-200 rounded w-64 mb-6 animate-pulse" />
        <div className="h-[400px] bg-gray-100 rounded animate-pulse" />
      </motion.div>
    );
  }

  if (data.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Evolution des prises de RDV</h3>
        <div className="h-[400px] flex items-center justify-center text-gray-400">
          Aucune donnee pour cette periode
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
    >
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Evolution des prises de RDV</h3>
        <p className="text-sm text-gray-500 mt-1">
          Nombre de rendez-vous obtenus par semaine
        </p>
      </div>

      <div className="w-full overflow-x-auto -mx-2">
        <div className="min-w-[600px] px-2">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                axisLine={{ stroke: '#E2E8F0' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Nombre de RDV', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#94A3B8' } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="rdvsCreated"
                stroke="#F43F5E"
                strokeWidth={2.5}
                fill="#F43F5E"
                fillOpacity={0.15}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};

export default StatisticsEvolutionChart;
